import React, { useEffect, useState, useCallback } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Receipt, FilePdf, FileXls, HandCoins, PiggyBank, Vault, Coins, Target } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (v, cur) => `${cur === "USD" ? "US$" : "RD$"} ${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KIND_META = {
  loan_payment: { key: "loan_payment_label", cls: "bg-emerald-100 text-emerald-700" },
  savings_deposit: { key: "savings_deposit_label", cls: "bg-teal-100 text-teal-700" },
  term_deposit: { key: "term_deposit_label", cls: "bg-indigo-100 text-indigo-700" },
};

export const CollectionsPanel = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [currency, setCurrency] = useState("all");
  const [kind, setKind] = useState("all");
  // meta
  const [summary, setSummary] = useState(null);
  const [goalInput, setGoalInput] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (start) p.start = start;
    if (end) p.end = end;
    if (currency !== "all") p.currency = currency;
    if (kind !== "all") p.kind = kind;
    return p;
  }, [start, end, currency, kind]);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.get("/admin/collections-report", { params: params() })
      .then((r) => setData(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [params]);

  const loadSummary = useCallback(() => {
    api.get("/admin/collections-summary").then((r) => { setSummary(r.data); setGoalInput(r.data.goal ? String(r.data.goal) : ""); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      await api.put("/admin/collections-goal", { amount: Number(goalInput) || 0 });
      toast.success(t("goal_saved"));
      loadSummary();
    } catch (e) { toast.error(t("error")); }
    finally { setSavingGoal(false); }
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.get(`/admin/collections-report/${fmt}`, { params: params(), responseType: "blob" });
      const isPdf = fmt === "pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: isPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `registro_cobros.${isPdf ? "pdf" : "xlsx"}`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("error")); }
    finally { setDownloading(""); }
  };

  const totals = data?.totals || {};
  const currencies = ["DOP", "USD"].filter((c) => totals[c]);
  const kindTabs = [
    { k: "all", label: "kind_all" },
    { k: "loan_payment", label: "kind_loans" },
    { k: "savings_deposit", label: "kind_savings" },
    { k: "term_deposit", label: "kind_term" },
  ];

  const goal = summary?.goal || 0;
  const monthDop = summary?.month_dop || 0;
  const progress = summary?.progress || 0;

  return (
    <div className="space-y-6" data-testid="collections-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-emerald-100 flex items-center justify-center"><Receipt size={24} weight="duotone" className="text-emerald-800" /></div>
          <div>
            <h2 className="font-display font-bold text-emerald-900 text-xl">{t("collections_title")}</h2>
            <p className="text-sm text-slate-500">{t("collections_sub")}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="collections-excel-btn" onClick={() => download("excel")} disabled={!!downloading || loading} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-2 h-11"><FileXls size={18} weight="bold" /> {downloading === "excel" ? t("generating") : t("download_excel")}</Button>
          <Button data-testid="collections-pdf-btn" onClick={() => download("pdf")} disabled={!!downloading || loading} className="bg-emerald-800 hover:bg-emerald-900 gap-2 h-11"><FilePdf size={18} weight="bold" /> {downloading === "pdf" ? t("generating") : t("download_pdf")}</Button>
        </div>
      </div>

      {/* Monthly goal */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="collections-goal-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Target size={20} weight="duotone" className="text-emerald-700" />
            <h3 className="font-display font-bold text-emerald-900">{t("monthly_goal")}</h3>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs text-slate-500">{t("goal_amount")} (RD$)</Label>
              <Input data-testid="collections-goal-input" type="number" min="0" step="100" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} className="h-10 w-[160px] font-mono-num" />
            </div>
            <Button data-testid="collections-goal-save" onClick={saveGoal} disabled={savingGoal} className="bg-emerald-800 hover:bg-emerald-900 h-10">{t("save")}</Button>
          </div>
        </div>
        {goal > 0 ? (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-500">{t("collected_month")}: <b className="text-emerald-900 font-mono-num">{money(monthDop, "DOP")}</b></span>
              <span className="text-slate-500">{t("goal_lower")}: <b className="text-emerald-900 font-mono-num">{money(goal, "DOP")}</b></span>
            </div>
            <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden" data-testid="collections-progress-bar">
              <div className={`h-full rounded-full transition-all ${progress >= 100 ? "bg-emerald-500" : "bg-emerald-700"}`} style={{ width: `${progress}%` }} />
            </div>
            <p className="text-sm text-slate-600 mt-1.5 font-medium">{progress}% {progress >= 100 ? "🎉 " + t("goal_reached") : t("of_goal")}</p>
          </div>
        ) : <p className="text-sm text-slate-400">{t("goal_hint")}</p>}
      </div>

      {/* Kind tabs + filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="collections-kind-tabs">
          {kindTabs.map((tab) => (
            <button key={tab.k} data-testid={`collections-kind-${tab.k}`} onClick={() => setKind(tab.k)}
              className={`text-sm px-3.5 py-1.5 rounded-full font-semibold transition-colors ${kind === tab.k ? "bg-emerald-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {t(tab.label)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-500">{t("from_date")}</Label>
            <Input data-testid="collections-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 w-[170px]" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-500">{t("to_date")}</Label>
            <Input data-testid="collections-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 w-[170px]" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-500">{t("currency")}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="collections-currency" className="h-10 w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all_currencies")}</SelectItem>
                <SelectItem value="DOP">RD$ (DOP)</SelectItem>
                <SelectItem value="USD">US$ (USD)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(start || end || currency !== "all") && (
            <Button data-testid="collections-clear" variant="outline" onClick={() => { setStart(""); setEnd(""); setCurrency("all"); }} className="h-10">{t("clear_filters")}</Button>
          )}
        </div>
      </div>

      {loading ? <p className="text-slate-500">{t("loading")}</p> : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="collections-error">
          <p className="text-red-700 font-semibold">{t("collections_load_error")}</p>
          <Button data-testid="collections-retry" onClick={load} className="mt-3 bg-emerald-800 hover:bg-emerald-900">{t("retry")}</Button>
        </div>
      ) : (
        <>
          {currencies.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {currencies.map((c) => {
                const b = totals[c];
                return (
                  <div key={c} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid={`collections-card-${c}`}>
                    <h3 className="font-display font-bold text-emerald-900 text-lg mb-4">{c === "USD" ? "US$ (USD)" : "RD$ (DOP)"}</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg bg-emerald-50 p-4">
                        <p className="text-xs uppercase tracking-wider text-emerald-600 flex items-center gap-1.5"><HandCoins size={14} weight="fill" /> {t("loan_payments_total")}</p>
                        <p className="font-mono-num text-lg font-bold text-emerald-900 mt-1">{money(b.loan_payments, c)}</p>
                      </div>
                      <div className="rounded-lg bg-teal-50 p-4">
                        <p className="text-xs uppercase tracking-wider text-teal-600 flex items-center gap-1.5"><PiggyBank size={14} weight="fill" /> {t("savings_deposits_total")}</p>
                        <p className="font-mono-num text-lg font-bold text-teal-800 mt-1">{money(b.savings_deposits, c)}</p>
                      </div>
                      <div className="rounded-lg bg-indigo-50 p-4">
                        <p className="text-xs uppercase tracking-wider text-indigo-600 flex items-center gap-1.5"><Vault size={14} weight="fill" /> {t("term_deposits_total")}</p>
                        <p className="font-mono-num text-lg font-bold text-indigo-800 mt-1">{money(b.term_deposits, c)}</p>
                      </div>
                      <div className="rounded-lg bg-amber-50 p-4">
                        <p className="text-xs uppercase tracking-wider text-amber-600 flex items-center gap-1.5"><Coins size={14} weight="fill" /> {t("total_collected")}</p>
                        <p className="font-mono-num text-lg font-bold text-amber-700 mt-1" data-testid={`collections-total-${c}`}>{money(b.total, c)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-3">{b.count} {t("movements_lower")}</p>
                  </div>
                );
              })}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto" data-testid="collections-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-gray-100">
                  <th className="px-4 py-2.5 font-medium">{t("date")}</th>
                  <th className="px-4 py-2.5 font-medium">{t("holder")}</th>
                  <th className="px-4 py-2.5 font-medium">{t("type")}</th>
                  <th className="px-4 py-2.5 font-medium">{t("reference")}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t("capital_col")}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t("interest_col")}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t("mv_amount")}</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="collections-empty">{t("no_collections")}</td></tr>
                ) : data.rows.map((r, i) => {
                  const meta = KIND_META[r.kind] || { key: r.kind, cls: "bg-slate-100 text-slate-600" };
                  return (
                    <tr key={`${r.reference}-${r.date}-${i}`} className="border-b border-gray-50 last:border-0" data-testid={`collections-row-${i}`}>
                      <td className="px-4 py-2.5 text-slate-600">{(r.date || "").slice(0, 10)}</td>
                      <td className="px-4 py-2.5 text-emerald-900 font-semibold">{r.member || "—"}</td>
                      <td className="px-4 py-2.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.cls}`}>{t(meta.key)}</span></td>
                      <td className="px-4 py-2.5 font-mono-num text-slate-500">{r.reference || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-slate-600">{r.capital != null ? money(r.capital, r.currency) : "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-slate-600">{r.interest != null ? money(r.interest, r.currency) : "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num font-bold text-emerald-800">{money(r.amount, r.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
